import { useScheme } from "@certn/cascadia-react";
import { css, cx } from "@emotion/css";
import hljs from "highlight.js/lib/core";
import bash from "highlight.js/lib/languages/bash";
import javascript from "highlight.js/lib/languages/javascript";
import json from "highlight.js/lib/languages/json";
import python from "highlight.js/lib/languages/python";
import typescript from "highlight.js/lib/languages/typescript";
import * as darkTheme from "highlight.js/styles/github-dark.css?inline";
import * as lightTheme from "highlight.js/styles/github.css?inline";
import { useEffect, useRef } from "react";

import { ExpandableContainer } from "./ExpandableContainer";

hljs.registerLanguage("bash", bash);
hljs.registerLanguage("javascript", javascript);
hljs.registerLanguage("json", json);
hljs.registerLanguage("python", python);
hljs.registerLanguage("typescript", typescript);

interface Props extends React.HTMLProps<HTMLDivElement> {
  language: string;
  hasNewLines?: boolean;
  testId?: string;
}
export const Code = ({ children, language, testId }: Props) => {
  const codeRef = useRef(null);

  const scheme = useScheme();
  useEffect(() => {
    if (codeRef.current) {
      hljs.highlightElement(codeRef.current);
    }
  }, [children, language]);

  const hasNewLines = /\n/.test(String(children));

  return hasNewLines ? (
    <ExpandableContainer>
      <pre className={styles.pre}>
        <code
          data-testid={testId}
          ref={codeRef}
          className={cx(
            language,
            styles.code,
            scheme === "light" ? styles.light : styles.dark
          )}
        >
          {children}
        </code>
      </pre>
    </ExpandableContainer>
  ) : (
    <code data-testid={testId}>{children}</code>
  );
};

const styles = {
  pre: css({ margin: "var(--space-0)" }),
  code: css({
    // Note: these styles have to be important to have precedence over the styles in the lib
    margin: "var(--space-0) !important",
    padding: "var(--space-0) !important",
    background: "none !important",
  }),
  dark: css(darkTheme),
  light: css(lightTheme),
};
